import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { Plus, MapPin, Calendar as CalIcon, User, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  name: "", main_character: "", aux_characters: "", date: "",
  venue: "", location: "", description: "", youtube_url: "", onedrive_url: "",
  status: "planned",
};

export default function EventsList() {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/events");
    setEvents(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        aux_characters: form.aux_characters
          ? form.aux_characters.split(",").map((s) => s.trim()).filter(Boolean)
          : [],
      };
      await api.post("/events", payload);
      toast.success("Event created");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-7xl">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[var(--text-2)] mb-2">
            Event Management
          </div>
          <h1 className="font-serif text-4xl">Productions</h1>
          <p className="text-sm text-[var(--text-2)] mt-3 max-w-xl">
            Each date is its own event instance &mdash; even for a recurring show, create a
            separate entry per performance date.
          </p>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="create-event-btn"
              className="rounded-none bg-[var(--primary)] hover:bg-[var(--primary)]/90 text-white"
            >
              <Plus className="w-4 h-4 mr-2" /> New Event
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">New Event</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-2 gap-4" data-testid="event-form">
              <Field label="Event name" required>
                <Input data-testid="event-name" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} required className="rounded-none" />
              </Field>
              <Field label="Date" required>
                <Input data-testid="event-date" type="date" value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })} required className="rounded-none" />
              </Field>
              <Field label="Main character / star">
                <Input data-testid="event-main" value={form.main_character}
                  onChange={(e) => setForm({ ...form, main_character: e.target.value })} className="rounded-none" />
              </Field>
              <Field label="Auxiliary (comma-separated)">
                <Input data-testid="event-aux" value={form.aux_characters}
                  onChange={(e) => setForm({ ...form, aux_characters: e.target.value })} className="rounded-none" />
              </Field>
              <Field label="Venue">
                <Input data-testid="event-venue" value={form.venue}
                  onChange={(e) => setForm({ ...form, venue: e.target.value })} className="rounded-none" />
              </Field>
              <Field label="Location">
                <Input data-testid="event-location" value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })} className="rounded-none" />
              </Field>
              <Field label="YouTube URL">
                <Input data-testid="event-youtube" value={form.youtube_url}
                  onChange={(e) => setForm({ ...form, youtube_url: e.target.value })} className="rounded-none" />
              </Field>
              <Field label="OneDrive Excel URL">
                <Input data-testid="event-onedrive" value={form.onedrive_url}
                  onChange={(e) => setForm({ ...form, onedrive_url: e.target.value })} className="rounded-none" />
              </Field>
              <div className="col-span-2">
                <Field label="Description">
                  <Textarea data-testid="event-description" value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-none" rows={3} />
                </Field>
              </div>
              <DialogFooter className="col-span-2">
                <Button type="submit" data-testid="event-submit" disabled={saving}
                  className="rounded-none bg-[var(--primary)] hover:bg-[var(--primary)]/90 text-white">
                  {saving ? "Saving…" : "Create event"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {events.length === 0 ? (
        <div className="card-sharp p-12 text-center text-[var(--text-2)]" data-testid="events-empty">
          No events yet. Click <b>New Event</b> to create your first production.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="events-grid">
          {events.map((e) => (
            <Link key={e.id} to={`/events/${e.id}`} data-testid={`event-card-${e.id}`}
              className="card-sharp p-6 group hover:border-[var(--primary)] transition-colors duration-150">
              <div className="flex items-start justify-between mb-4">
                <span className="text-[10px] mono uppercase tracking-widest text-[var(--text-2)]">
                  {e.status || "planned"}
                </span>
                <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <h3 className="font-serif text-2xl mb-3 leading-tight">{e.name}</h3>
              <div className="text-sm text-[var(--text-2)] space-y-1">
                <div className="flex items-center gap-2"><CalIcon className="w-3 h-3" /> <span className="mono">{e.date}</span></div>
                {e.venue && <div className="flex items-center gap-2"><MapPin className="w-3 h-3" /> {e.venue}{e.location ? `, ${e.location}` : ""}</div>}
                {e.main_character && <div className="flex items-center gap-2"><User className="w-3 h-3" /> {e.main_character}</div>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest">
        {label}{required && <span className="text-[var(--accent)] ml-1">*</span>}
      </Label>
      {children}
    </div>
  );
}
